import type { AddressFromToFilter } from 'types/api/address';
import type { MethodType } from 'ui/address/contract/methods/types';
import type { SelectOption } from 'ui/shared/select/types';

export const ADDRESS_PAGE_KEYS = {
  title: {
    addressDetails: 'address.shell.title.addressDetails',
    contractDetails: 'address.shell.title.contractDetails',
  },
  actions: {
    copyToClipboard: 'address.shell.actions.copyToClipboard',
    copied: 'address.shell.actions.copied',
    addToWatchList: 'address.shell.actions.addToWatchList',
    removeFromWatchList: 'address.shell.actions.removeFromWatchList',
    downloadCsv: 'address.shell.actions.downloadCsv',
  },
  aria: {
    showQrCode: 'address.shell.aria.showQrCode',
    tokenSelect: 'address.shell.aria.tokenSelect',
    openContractDetails: 'address.shell.aria.openContractDetails',
  },
  tooltip: {
    viewQrCode: 'address.shell.tooltip.viewQrCode',
  },
  modal: {
    addressQrCode: 'address.shell.modal.addressQrCode',
    qrCodeError: 'address.shell.modal.qrCodeError',
  },
} as const;

export const ADDRESS_TAB_KEYS = {
  mud: 'address.tabs.mud',
  transactions: 'address.tabs.transactions',
  accountHistory: 'address.tabs.accountHistory',
  userOps: 'address.tabs.userOps',
  withdrawals: 'address.tabs.withdrawals',
  tokenTransfers: 'address.tabs.tokenTransfers',
  tokens: 'address.tabs.tokens',
  internalTxns: 'address.tabs.internalTxns',
  epochRewards: 'address.tabs.epochRewards',
  coinBalanceHistory: 'address.tabs.coinBalanceHistory',
  blocksValidated: 'address.tabs.blocksValidated',
  logs: 'address.tabs.logs',
  contract: 'address.tabs.contract',
} as const;

export const ADDRESS_TAG_KEYS = {
  eoa: 'address.tags.eoa',
  validator: 'address.tags.validator',
  proxy: 'address.tags.proxy',
  token: 'address.tags.token',
  multisigSafe: 'address.tags.multisigSafe',
  smartContractWallet: 'address.tags.smartContractWallet',
  mudWorld: 'address.tags.mudWorld',
  xstarLevel: 'address.tags.xstarLevel',
  xstarTooltipTitle: 'address.tags.xstarTooltipTitle',
  xstarTooltipDescription: 'address.tags.xstarTooltipDescription',
} as const;

export const ADDRESS_DETAILS_KEYS = {
  sections: {
    overview: 'address.details.sections.overview',
    moreInfo: 'address.details.sections.moreInfo',
    blockchainInfo: 'address.details.sections.blockchainInfo',
  },
  fields: {
    tokenName: 'address.details.fields.tokenName',
    contractName: 'address.details.fields.contractName',
    validatorName: 'address.details.fields.validatorName',
    creator: 'address.details.fields.creator',
    tokens: 'address.details.fields.tokens',
    balance: 'address.details.fields.balance',
    netWorth: 'address.details.fields.netWorth',
    lastBalanceUpdate: 'address.details.fields.lastBalanceUpdate',
    transactions: 'address.details.fields.transactions',
    transfers: 'address.details.fields.transfers',
    gasUsed: 'address.details.fields.gasUsed',
  },
  tooltip: {
    tokenName: 'address.details.tooltip.tokenName',
    contractName: 'address.details.tooltip.contractName',
    validatorName: 'address.details.tooltip.validatorName',
    creator: 'address.details.tooltip.creator',
    tokens: 'address.details.tooltip.tokens',
    balance: 'address.details.tooltip.balance',
    netWorth: 'address.details.tooltip.netWorth',
    lastBalanceUpdate: 'address.details.tooltip.lastBalanceUpdate',
    transactions: 'address.details.tooltip.transactions',
    transfers: 'address.details.tooltip.transfers',
    gasUsed: 'address.details.tooltip.gasUsed',
    base16Hash: 'address.details.tooltip.base16Hash',
    bech32Hash: 'address.details.tooltip.bech32Hash',
  },
  domains: {
    tooltip: 'address.details.domains.tooltip',
    aria: 'address.details.domains.aria',
    countOne: 'address.details.domains.countOne',
    countOther: 'address.details.domains.countOther',
    primary: 'address.details.domains.primary',
    expires: 'address.details.domains.expires',
    ownedByAddress: 'address.details.domains.ownedByAddress',
    resolvedToAddress: 'address.details.domains.resolvedToAddress',
    moreResults: 'address.details.domains.moreResults',
    disclaimer: 'address.details.domains.disclaimer',
  },
  filecoinActorTypes: {
    account: 'address.details.filecoinActorTypes.account',
    cron: 'address.details.filecoinActorTypes.cron',
    datacap: 'address.details.filecoinActorTypes.datacap',
    eam: 'address.details.filecoinActorTypes.eam',
    ethaccount: 'address.details.filecoinActorTypes.ethaccount',
    evm: 'address.details.filecoinActorTypes.evm',
    init: 'address.details.filecoinActorTypes.init',
    market: 'address.details.filecoinActorTypes.market',
    miner: 'address.details.filecoinActorTypes.miner',
    multisig: 'address.details.filecoinActorTypes.multisig',
    paych: 'address.details.filecoinActorTypes.paych',
    placeholder: 'address.details.filecoinActorTypes.placeholder',
    power: 'address.details.filecoinActorTypes.power',
    reward: 'address.details.filecoinActorTypes.reward',
    system: 'address.details.filecoinActorTypes.system',
    verifreg: 'address.details.filecoinActorTypes.verifreg',
  },
  format: {
    base16Hash: 'address.details.format.base16Hash',
    prefixedHash: 'address.details.format.prefixedHash',
  },
  inline: {
    atTxn: 'address.details.inline.atTxn',
    multichain: 'address.details.inline.multichain',
  },
} as const;

export const ADDRESS_LIST_KEYS = {
  fields: {
    block: 'address.lists.fields.block',
    age: 'address.lists.fields.age',
    txn: 'address.lists.fields.txn',
    parentTxnHash: 'address.lists.fields.parentTxnHash',
    type: 'address.lists.fields.type',
    fromTo: 'address.lists.fields.fromTo',
    gasUsed: 'address.lists.fields.gasUsed',
    reward: 'address.lists.fields.reward',
    value: 'address.lists.fields.value',
    valueWithSymbol: 'address.lists.fields.valueWithSymbol',
    asset: 'address.lists.fields.asset',
    contractAddress: 'address.lists.fields.contractAddress',
    price: 'address.lists.fields.price',
    quantity: 'address.lists.fields.quantity',
    balanceWithSymbol: 'address.lists.fields.balanceWithSymbol',
    delta: 'address.lists.fields.delta',
    epochNumber: 'address.lists.fields.epochNumber',
    rewardType: 'address.lists.fields.rewardType',
    associatedAddress: 'address.lists.fields.associatedAddress',
  },
  txs: {
    socketClose: 'address.lists.txs.socketClose',
    socketError: 'address.lists.txs.socketError',
  },
  accountHistory: {
    empty: 'address.lists.accountHistory.empty',
    emptyFiltered: 'address.lists.accountHistory.emptyFiltered',
  },
  tokenTransfers: {
    empty: 'address.lists.tokenTransfers.empty',
    emptyFiltered: 'address.lists.tokenTransfers.emptyFiltered',
    filteredByToken: 'address.lists.tokenTransfers.filteredByToken',
    socketClose: 'address.lists.tokenTransfers.socketClose',
    socketError: 'address.lists.tokenTransfers.socketError',
  },
  internalTxs: {
    empty: 'address.lists.internalTxs.empty',
    emptyFiltered: 'address.lists.internalTxs.emptyFiltered',
  },
  logs: {
    empty: 'address.lists.logs.empty',
  },
  blocksValidated: {
    empty: 'address.lists.blocksValidated.empty',
    socketError: 'address.lists.blocksValidated.socketError',
  },
  withdrawals: {
    empty: 'address.lists.withdrawals.empty',
  },
  epochRewards: {
    empty: 'address.lists.epochRewards.empty',
  },
  coinBalance: {
    empty: 'address.lists.coinBalance.empty',
    chartTitle: 'address.lists.coinBalance.chartTitle',
    insufficientData: 'address.lists.coinBalance.insufficientData',
  },
} as const;

export const ADDRESS_TOKEN_KEYS = {
  tabs: {
    nfts: 'address.tokensView.tabs.nfts',
  },
  filters: {
    byCollection: 'address.tokensView.filters.byCollection',
    list: 'address.tokensView.filters.list',
    searchByName: 'address.tokensView.filters.searchByName',
  },
  fields: {
    netWorth: 'address.tokensView.fields.netWorth',
    balance: 'address.tokensView.fields.balance',
    tokens: 'address.tokensView.fields.tokens',
    id: 'address.tokensView.fields.id',
    quantityShort: 'address.tokensView.fields.quantityShort',
  },
  empty: {
    selectedType: 'address.tokensView.empty.selectedType',
    filteredToken: 'address.tokensView.empty.filteredToken',
    noTokens: 'address.tokensView.empty.noTokens',
    noMatches: 'address.tokensView.empty.noMatches',
  },
  actions: {
    viewInCollection: 'address.tokensView.actions.viewInCollection',
    viewAllNfts: 'address.tokensView.actions.viewAllNfts',
    showAllTokens: 'address.tokensView.actions.showAllTokens',
  },
  aria: {
    sortTypeTokens: 'address.tokensView.aria.sortTypeTokens',
  },
  count: {
    itemOne: 'address.tokensView.count.itemOne',
    itemOther: 'address.tokensView.count.itemOther',
    tokenOne: 'address.tokensView.count.tokenOne',
    tokenOther: 'address.tokensView.count.tokenOther',
    typeTokens: 'address.tokensView.count.typeTokens',
  },
} as const;

export const ADDRESS_CONTRACT_KEYS = {
  tabs: {
    code: 'address.contractView.tabs.code',
    readWriteContract: 'address.contractView.tabs.readWriteContract',
    readWriteProxy: 'address.contractView.tabs.readWriteProxy',
    customAbi: 'address.contractView.tabs.customAbi',
    mudSystem: 'address.contractView.tabs.mudSystem',
    readContract: 'address.contractView.tabs.readContract',
    writeContract: 'address.contractView.tabs.writeContract',
    readProxy: 'address.contractView.tabs.readProxy',
    writeProxy: 'address.contractView.tabs.writeProxy',
    readCustomMethods: 'address.contractView.tabs.readCustomMethods',
    writeCustomMethods: 'address.contractView.tabs.writeCustomMethods',
    compiler: 'address.contractView.tabs.compiler',
    abi: 'address.contractView.tabs.abi',
    bytecode: 'address.contractView.tabs.bytecode',
  },
  fields: {
    currentContract: 'address.contractView.fields.currentContract',
    sourceCode: 'address.contractView.fields.sourceCode',
    implementationAddress: 'address.contractView.fields.implementationAddress',
    systemAddress: 'address.contractView.fields.systemAddress',
    contractName: 'address.contractView.fields.contractName',
    compilerVersion: 'address.contractView.fields.compilerVersion',
    zkCompilerVersion: 'address.contractView.fields.zkCompilerVersion',
    evmVersion: 'address.contractView.fields.evmVersion',
    license: 'address.contractView.fields.license',
    optimizationEnabled: 'address.contractView.fields.optimizationEnabled',
    optimizationMode: 'address.contractView.fields.optimizationMode',
    optimizationRuns: 'address.contractView.fields.optimizationRuns',
    packageName: 'address.contractView.fields.packageName',
    verifiedAt: 'address.contractView.fields.verifiedAt',
    contractFilePath: 'address.contractView.fields.contractFilePath',
    securityAudit: 'address.contractView.fields.securityAudit',
    constructorArguments: 'address.contractView.fields.constructorArguments',
  },
  actions: {
    update: 'address.contractView.actions.update',
    addCustomAbi: 'address.contractView.actions.addCustomAbi',
    verifyAndPublish: 'address.contractView.actions.verifyAndPublish',
    viewUmlDiagram: 'address.contractView.actions.viewUmlDiagram',
    expand: 'address.contractView.actions.expand',
    collapse: 'address.contractView.actions.collapse',
    reset: 'address.contractView.actions.reset',
    openIn: 'address.contractView.actions.openIn',
    submitAudit: 'address.contractView.actions.submitAudit',
    sendRequest: 'address.contractView.actions.sendRequest',
    connectWallet: 'address.contractView.actions.connectWallet',
    disconnect: 'address.contractView.actions.disconnect',
    read: 'address.contractView.actions.read',
    write: 'address.contractView.actions.write',
    simulate: 'address.contractView.actions.simulate',
    copyCalldata: 'address.contractView.actions.copyCalldata',
    self: 'address.contractView.actions.self',
    max: 'address.contractView.actions.max',
    nowPlusOneHour: 'address.contractView.actions.nowPlusOneHour',
    add: 'address.contractView.actions.add',
    remove: 'address.contractView.actions.remove',
  },
  batchPaste: {
    title: 'address.contractView.batchPaste.title',
    placeholder: 'address.contractView.batchPaste.placeholder',
    invalidAddresses: 'address.contractView.batchPaste.invalidAddresses',
    open: 'address.contractView.batchPaste.open',
    close: 'address.contractView.batchPaste.close',
  },
  labels: {
    contractInformation: 'address.contractView.labels.contractInformation',
    compilerSettings: 'address.contractView.labels.compilerSettings',
    contractAbi: 'address.contractView.labels.contractAbi',
    contractCreationCode: 'address.contractView.labels.contractCreationCode',
    deployedBytecode: 'address.contractView.labels.deployedBytecode',
    contractSourceCode: 'address.contractView.labels.contractSourceCode',
    redactors: 'address.contractView.labels.redactors',
    disconnected: 'address.contractView.labels.disconnected',
    connectedTo: 'address.contractView.labels.connectedTo',
    partial: 'address.contractView.labels.partial',
    exact: 'address.contractView.labels.exact',
    sourceCodeVerified: 'address.contractView.labels.sourceCodeVerified',
    contractAnalyzedBy: 'address.contractView.labels.contractAnalyzedBy',
    vulnerabilitiesDistribution: 'address.contractView.labels.vulnerabilitiesDistribution',
  },
  filters: {
    all: 'address.contractView.filters.all',
    read: 'address.contractView.filters.read',
    write: 'address.contractView.filters.write',
    searchByMethodName: 'address.contractView.filters.searchByMethodName',
  },
  notice: {
    noPublicFunctions: 'address.contractView.notice.noPublicFunctions',
    noPublicTypedFunctions: 'address.contractView.notice.noPublicTypedFunctions',
    customAbiDescription: 'address.contractView.notice.customAbiDescription',
    selfDestructed: 'address.contractView.notice.selfDestructed',
    noMethodMatches: 'address.contractView.notice.noMethodMatches',
    customAbiHintPrefix: 'address.contractView.notice.customAbiHintPrefix',
    customAbiHintSuffix: 'address.contractView.notice.customAbiHintSuffix',
    blueprintPrefix: 'address.contractView.notice.blueprintPrefix',
    changedBytecode: 'address.contractView.notice.changedBytecode',
    verifiedTwinPrefix: 'address.contractView.notice.verifiedTwinPrefix',
    verifiedTwinSuffix: 'address.contractView.notice.verifiedTwinSuffix',
    verifyPageSuffix: 'address.contractView.notice.verifyPageSuffix',
    bytecodeDbVerified: 'address.contractView.notice.bytecodeDbVerified',
    bytecodeDbPartiallyVerified: 'address.contractView.notice.bytecodeDbPartiallyVerified',
    sourcifyVerified: 'address.contractView.notice.sourcifyVerified',
    sourcifyPartiallyVerified: 'address.contractView.notice.sourcifyPartiallyVerified',
    proxyDetectedVia: 'address.contractView.notice.proxyDetectedVia',
    proxyDetectedPrefix: 'address.contractView.notice.proxyDetectedPrefix',
    proxyDetectedSuffix: 'address.contractView.notice.proxyDetectedSuffix',
    externalLibraryWarning: 'address.contractView.notice.externalLibraryWarning',
    noWalletClient: 'address.contractView.notice.noWalletClient',
    fallbackHint: 'address.contractView.notice.fallbackHint',
    receiveHint: 'address.contractView.notice.receiveHint',
    methodParseError: 'address.contractView.notice.methodParseError',
    walletAddressHint: 'address.contractView.notice.walletAddressHint',
  },
  tooltip: {
    license: 'address.contractView.tooltip.license',
    visualize: 'address.contractView.tooltip.visualize',
  },
  aria: {
    viewExternalLibraries: 'address.contractView.aria.viewExternalLibraries',
    openSourceCodeInIde: 'address.contractView.aria.openSourceCodeInIde',
    ideIcon: 'address.contractView.aria.ideIcon',
  },
  proxy: {
    minimalProxy: 'address.contractView.proxy.minimalProxy',
    proxyStorageSlots: 'address.contractView.proxy.proxyStorageSlots',
    universalUpgradeableProxy: 'address.contractView.proxy.universalUpgradeableProxy',
    diamondProxy: 'address.contractView.proxy.diamondProxy',
    eternalStorage: 'address.contractView.proxy.eternalStorage',
  },
  externalLibraries: {
    countOne: 'address.contractView.externalLibraries.countOne',
    countOther: 'address.contractView.externalLibraries.countOther',
    title: 'address.contractView.externalLibraries.title',
  },
  security: {
    viewFullReport: 'address.contractView.security.viewFullReport',
    viewSourcify: 'address.contractView.security.viewSourcify',
  },
  audit: {
    ownerCheckbox: 'address.contractView.audit.ownerCheckbox',
    submitterName: 'address.contractView.audit.submitterName',
    submitterEmail: 'address.contractView.audit.submitterEmail',
    projectName: 'address.contractView.audit.projectName',
    projectUrl: 'address.contractView.audit.projectUrl',
    auditCompanyName: 'address.contractView.audit.auditCompanyName',
    auditReportUrl: 'address.contractView.audit.auditReportUrl',
    auditPublishDate: 'address.contractView.audit.auditPublishDate',
    comment: 'address.contractView.audit.comment',
    successTitle: 'address.contractView.audit.successTitle',
    successDescription: 'address.contractView.audit.successDescription',
    errorTitle: 'address.contractView.audit.errorTitle',
    genericError: 'address.contractView.audit.genericError',
  },
  methods: {
    sendNativeCoin: 'address.contractView.methods.sendNativeCoin',
    unnamedArgument: 'address.contractView.methods.unnamedArgument',
  },
  results: {
    viewTransactionDetails: 'address.contractView.results.viewTransactionDetails',
    transactionConfirmed: 'address.contractView.results.transactionConfirmed',
    waitingForConfirmation: 'address.contractView.results.waitingForConfirmation',
    errorPrefix: 'address.contractView.results.errorPrefix',
  },
  validation: {
    required: 'address.contractView.validation.required',
    invalidAddress: 'address.contractView.validation.invalidAddress',
    invalidChecksum: 'address.contractView.validation.invalidChecksum',
    invalidInteger: 'address.contractView.validation.invalidInteger',
    integerRange: 'address.contractView.validation.integerRange',
    invalidBoolean: 'address.contractView.validation.invalidBoolean',
    invalidBytes: 'address.contractView.validation.invalidBytes',
    bytesLength: 'address.contractView.validation.bytesLength',
  },
} as const;

export const ADDRESS_MUD_KEYS = {
  breadcrumb: {
    world: 'address.mud.breadcrumb.world',
  },
  fields: {
    fullName: 'address.mud.fields.fullName',
    tableId: 'address.mud.fields.tableId',
    type: 'address.mud.fields.type',
    key: 'address.mud.fields.key',
    value: 'address.mud.fields.value',
    field: 'address.mud.fields.field',
    modified: 'address.mud.fields.modified',
  },
  filters: {
    searchByTable: 'address.mud.filters.searchByTable',
  },
  actions: {
    hideColumns: 'address.mud.actions.hideColumns',
    showAllColumns: 'address.mud.actions.showAllColumns',
  },
  aria: {
    showHideColumns: 'address.mud.aria.showHideColumns',
    viewSchema: 'address.mud.aria.viewSchema',
  },
  empty: {
    tables: 'address.mud.empty.tables',
    tablesFiltered: 'address.mud.empty.tablesFiltered',
    recordsPrefix: 'address.mud.empty.recordsPrefix',
    thisTable: 'address.mud.empty.thisTable',
    recordsFiltered: 'address.mud.empty.recordsFiltered',
  },
} as const;

export const ADDRESS_ACCOUNT_HISTORY_FILTER_OPTION_KEYS: Array<{ value: 'all' | 'received' | 'sent'; label: string; labelKey: string }> = [
  { value: 'all', label: 'All', labelKey: 'address.filters.accountHistoryAll' },
  { value: 'received', label: 'Received from', labelKey: 'address.filters.receivedFrom' },
  { value: 'sent', label: 'Sent to', labelKey: 'address.filters.sentTo' },
];

export const ADDRESS_FILTER_OPTION_KEYS: Array<SelectOption<NonNullable<AddressFromToFilter> | 'all'> & { labelKey: string }> = [
  { value: 'all', label: 'All', labelKey: 'address.filters.all' },
  { value: 'from', label: 'Outgoing transactions', labelKey: 'address.filters.outgoingTxs' },
  { value: 'to', label: 'Incoming transactions', labelKey: 'address.filters.incomingTxs' },
];

export const CONTRACT_METHOD_FILTER_OPTION_KEYS: Array<{ value: MethodType; title: string; titleKey: string }> = [
  { value: 'all', title: 'All', titleKey: ADDRESS_CONTRACT_KEYS.filters.all },
  { value: 'read', title: 'Read', titleKey: ADDRESS_CONTRACT_KEYS.filters.read },
  { value: 'write', title: 'Write', titleKey: ADDRESS_CONTRACT_KEYS.filters.write },
];
